import { useEffect, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Send, FileText, ShieldCheck, Check, X, ArrowRight, ChevronRight } from "lucide-react";
import { API_BASE } from "@/lib/site";
import { getTrackingPayload } from "@/lib/utm";
import { initTracking, trackConversion, pushLeadDataLayer } from "@/lib/tracking";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { ResourcesIllustration } from "@/components/illustrations/FintechIllustrations";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BUSINESS_TYPES = [
  "Restaurant", "Deli / Convenience", "Grocery", "Smoke Shop", "Retail", "Salon / Spa", "Service Business", "E-Commerce", "Other",
];
const VOLUME = [
  "Under $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000+",
];
const INTEREST = [
  "Cash Discount", "Traditional Pricing", "Free POS", "POS Quote", "Not Sure",
];

/* ────────────────────────────────────────────────────────────
   UNIFIED HERO — matches the rest of the website
   ──────────────────────────────────────────────────────────── */
function UnifiedHero({ eyebrowLabel, title, subtitle, breadcrumb }) {
  return (
    <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
      <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
      <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
      <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
      <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
      <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
      <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

      <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

      <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
        {breadcrumb && (
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">{breadcrumb}</span>
          </nav>
        )}
        <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                {eyebrowLabel}
              </div>
              <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[4.25rem] xl:text-[4.5rem] font-extrabold leading-[1.05] text-white uppercase tracking-[-0.03em]">
                {title}
              </h1>
              <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                {subtitle}
              </p>
            </div>
          </Reveal>

          <Reveal variant="slide-right" delay={120}>
            <div className="relative">
              <ResourcesIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function SavingsAnalysis() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    phone: "",
    email: "",
    business_type: "",
    monthly_volume: "",
    current_processor: "",
    current_pos: "",
    interested_in: "",
    message: "",
  });
  const [file, setFile] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null); // { storage_path, original_filename }
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Load tracking pixels once on mount; conversion fires only on successful submit.
  useEffect(() => { initTracking(); }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSelect = (k) => (v) => setForm({ ...form, [k]: v });

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB).");
      return;
    }
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API_BASE}/uploads/statement`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadInfo({ storage_path: res.data.storage_path, original_filename: res.data.original_filename });
      toast.success("Statement uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setUploadInfo(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.phone || !form.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        statement_storage_path: uploadInfo?.storage_path || null,
        statement_filename: uploadInfo?.original_filename || null,
        tracking: getTrackingPayload(),
      };
      const r = await axios.post(`${API_BASE}/savings-analysis`, payload);
      trackConversion("SavingsAnalysisLead");
      pushLeadDataLayer("lead_savings_analysis_submit", "savings_analysis", r?.data?.id);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="savings-success">
        <UnifiedHero
          eyebrowLabel="Submitted"
          title="Got it. We'll be in touch shortly."
          subtitle="Thanks for reaching out. A Swipetronics specialist will review your information and get back to you — usually within one business day."
        />
        <section className="section container-swipe">
          <div className="max-w-2xl bg-white border border-line rounded-2xl p-8">
            <div className="w-12 h-12 rounded-full bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center">
              <Check className="w-6 h-6" strokeWidth={3} />
            </div>
            <h2 className="mt-5 font-display-tight text-2xl font-black text-[var(--swipe-navy)]">Thank you for contacting Swipetronics.</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              We received your information and will review it shortly. A specialist will contact you to discuss your processing options, POS needs, and possible savings.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/" className="btn-secondary-swipe">Back to home</a>
              <a href="/resources" className="btn-outline-swipe">Browse resources</a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="savings-analysis-page" data-prerender-ready="true">
      <Seo {...getSeoForPath("/savings-analysis")} />
      <UnifiedHero
        eyebrowLabel="Free Statement Review"
        title="Free Savings Analysis"
        subtitle="Get a clear review of your current processing costs and see if Swipetronics can help your business save money each month."
        breadcrumb="Savings Analysis"
      />

      <section className="section container-swipe">
        <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10">
          <form onSubmit={submit} className="bg-white border border-line rounded-2xl p-8 lg:p-10" data-testid="savings-form">
            <div className="overline mb-3">Your information</div>
            <h3 className="font-display-tight text-2xl font-black text-[var(--swipe-navy)]">Tell us about your business.</h3>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="s-business">Business name *</Label>
                <Input id="s-business" value={form.business_name} onChange={update("business_name")} required className="mt-2" data-testid="savings-input-business-name" />
              </div>
              <div>
                <Label htmlFor="s-contact">Contact name *</Label>
                <Input id="s-contact" value={form.contact_name} onChange={update("contact_name")} required className="mt-2" data-testid="savings-input-contact-name" />
              </div>
              <div>
                <Label htmlFor="s-phone">Phone *</Label>
                <Input id="s-phone" value={form.phone} onChange={update("phone")} required className="mt-2" data-testid="savings-input-phone" />
              </div>
              <div>
                <Label htmlFor="s-email">Email *</Label>
                <Input id="s-email" type="email" value={form.email} onChange={update("email")} required className="mt-2" data-testid="savings-input-email" />
              </div>
              <div>
                <Label htmlFor="s-btype">Business type</Label>
                <Select value={form.business_type} onValueChange={setSelect("business_type")}>
                  <SelectTrigger className="mt-2" id="s-btype" data-testid="savings-select-business-type"><SelectValue placeholder="Select business type" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="s-vol">Approximate monthly card volume</Label>
                <Select value={form.monthly_volume} onValueChange={setSelect("monthly_volume")}>
                  <SelectTrigger className="mt-2" id="s-vol" data-testid="savings-select-volume"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    {VOLUME.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="s-cp">Current processor</Label>
                <Input id="s-cp" value={form.current_processor} onChange={update("current_processor")} placeholder="e.g., Square, Heartland" className="mt-2" data-testid="savings-input-processor" />
              </div>
              <div>
                <Label htmlFor="s-pos">Current POS system</Label>
                <Input id="s-pos" value={form.current_pos} onChange={update("current_pos")} placeholder="e.g., Clover, Toast" className="mt-2" data-testid="savings-input-pos" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="s-int">Interested in</Label>
                <Select value={form.interested_in} onValueChange={setSelect("interested_in")}>
                  <SelectTrigger className="mt-2" id="s-int" data-testid="savings-select-interest"><SelectValue placeholder="What are you most interested in?" /></SelectTrigger>
                  <SelectContent>
                    {INTEREST.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-7">
              <Label>Optional: Upload latest merchant statement (PDF, JPG, PNG)</Label>
              <div className="mt-2">
                {!file ? (
                  <label htmlFor="s-file" className="flex items-center justify-center gap-3 px-5 py-8 border-2 border-dashed border-line rounded-xl cursor-pointer hover:border-[var(--swipe-accent)] hover:bg-[var(--swipe-muted)] transition-colors" data-testid="savings-file-label">
                    <Upload className="w-5 h-5 text-slate-500" />
                    <div className="text-sm text-slate-600"><span className="font-semibold text-[var(--swipe-navy)]">Click to upload</span> &middot; PDF or image &middot; 10MB max</div>
                    <input id="s-file" type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onPickFile} data-testid="savings-file-input" />
                  </label>
                ) : (
                  <div className="flex items-center justify-between gap-3 p-4 border border-line rounded-xl bg-[var(--swipe-muted)]">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-[var(--swipe-accent)] shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[var(--swipe-navy)] truncate">{file.name}</div>
                        <div className="text-xs text-slate-500">{uploading ? "Uploading…" : "Ready"}</div>
                      </div>
                    </div>
                    <button type="button" onClick={clearFile} className="p-1.5 rounded hover:bg-white"><X className="w-4 h-4 text-slate-500" /></button>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Uploading a statement is optional. You can still request an estimate without uploading sensitive documents.
              </p>
            </div>

            <div className="mt-5">
              <Label htmlFor="s-msg">Anything else we should know?</Label>
              <Textarea id="s-msg" value={form.message} onChange={update("message")} rows={4} className="mt-2" data-testid="savings-input-message" />
            </div>

            <button type="submit" disabled={submitting || uploading} className="btn-primary-swipe mt-8 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" data-testid="savings-submit">
              {submitting ? "Submitting…" : <>Request my free savings analysis <Send className="w-4 h-4" /></>}
            </button>
            <p className="mt-4 text-[11.5px] text-slate-500 leading-[1.6]" data-testid="savings-consent">
              By submitting this form, you agree to be contacted by Swipetronics regarding your request. Message and data rates may apply. Reply STOP to opt out of text messages.
            </p>
          </form>

          <aside className="space-y-5">
            <div className="bg-[var(--swipe-navy)] text-white rounded-2xl p-7 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[var(--swipe-accent)]/20 blur-3xl" />
              <div className="relative">
                <ShieldCheck className="w-7 h-7 text-[var(--swipe-accent)]" />
                <h4 className="mt-4 font-display text-xl font-black" style={{ color: "#FFFFFF" }}>Your information is treated with care.</h4>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">We only use the details you share to prepare your analysis. We don&apos;t sell your data.</p>
              </div>
            </div>
            <div className="bg-white border border-line rounded-2xl p-7">
              <div className="overline mb-3">What happens next</div>
              <ol className="space-y-4">
                {[
                  ["We review your information", "Usually within one business day."],
                  ["We send a side-by-side", "Your current setup vs Swipetronics options."],
                  ["You decide", "No pressure, no obligation. Switch only if it makes sense."],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</div>
                    <div>
                      <div className="font-display font-bold text-[var(--swipe-navy)]">{t}</div>
                      <div className="text-sm text-slate-600">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
